import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { LayoutData } from "@ServerTypes/portal/portal.schema";

// State shape is the server `LayoutData` (from `@ServerTypes/portal/portal.schema`),
// the single source of truth — the portal `metaData` payload returned by `GET /portal/getLayoutData`.
const initialState: LayoutData = {};

const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    setLayout: (
      state: LayoutData,
      action: PayloadAction<Partial<LayoutData> | Record<string, unknown> | undefined>,
    ) => {
      Object.assign(state, action.payload);
    },
  },
});

export const { setLayout } = layoutSlice.actions;

export default layoutSlice.reducer;

/**
 * Whether `GET /portal/getLayoutData` has answered on this page load (S-V5-76): "pending" until its first answer,
 * then "loaded" (the slice above holds the portal's layout) or "failed". The v5 shell draws the layout-gated search
 * chip while the layout is pending, so a first load's screen skeleton has the chip the screen will have (the flag is
 * on for the portals that set it, `SHOW_GLOBAL_SEARCH`); a later refetch never returns it to pending.
 */
export type LayoutStatus = "pending" | "loaded" | "failed";

// The lazy initialiser makes `State` the whole union: a union-annotated const narrows to its initialiser at the
// call, so `createSlice` would infer `"pending"` alone and reject the settling reducer.
const layoutStatusSlice = createSlice({
  name: "layoutStatus",
  initialState: (): LayoutStatus => "pending",
  reducers: {
    settleLayoutStatus: (state: LayoutStatus, action: PayloadAction<"loaded" | "failed">): LayoutStatus =>
      // A failed refetch keeps the layout that already landed.
      state === "loaded" ? state : action.payload,
  },
});

export const { settleLayoutStatus } = layoutStatusSlice.actions;

export const layoutStatus = layoutStatusSlice.reducer;
